/**
 * Share Helpers - Reusable utilities for sharing profiles, questions, and events
 *
 * Provides consistent share functionality with:
 * - Native Web Share API with clipboard fallback
 * - Social platform sharing (WhatsApp, Telegram, LinkedIn, Facebook, X/Twitter, Email)
 * - ?source=app_share parameter for attribution tracking
 * - Amplitude analytics integration
 * - Toast notifications for user feedback
 *
 * This module re-exports types from shareConfig.ts and provides the execution layer
 * for actually performing share actions.
 */

import { toast } from '@hooks/use-toast';
import { getWebOrigin, isNativePlatform } from '@lib/platform';
import { getCurrentReferralCode } from '@lib/referralCode';
import { logger } from './logger';
import { copyToClipboard } from './clipboard';

// Re-export all types and enums from shareConfig
export {
  SocialPlatform,
  ShareEntityType,
  TextPlatform,
  ShareScenario,
  type ShareContext,
  type ProfileShareContext,
  type QuestionShareContext,
  type EventShareContext,
  type CommunityShareContext,
  type AppShareContext,
  type ShareSheetUIConfig,
  getShareSheetUI,
  getShareMessage,
  getEmailTemplates,
  createQuestionShareContext,
  createEventShareContext,
  createProfileShareContext,
  createCommunityShareContext,
  createAppShareContext,
  registerQuestionConfig,
  registerEventConfig,
  registerProfileConfig,
  registerCommunityConfig,
  registerAppConfig,
} from './shareConfig';

import {
  SocialPlatform,
  ShareEntityType,
  TextPlatform,
  type ShareContext,
  getShareMessage,
  getEmailTemplates,
} from './shareConfig';

// =============================================================================
// CONSTANTS
// =============================================================================

// Re-export MEETVERSE_IDENTIFIER for use in share contexts
export { MEETVERSE_IDENTIFIER } from '../constants/events';

// =============================================================================
// SHARE METADATA - For analytics tracking
// =============================================================================

/**
 * Base metadata for all share events
 */
interface BaseShareMetadata {
  type: ShareEntityType;
  id: string;
  slug?: string | null;
  sharerId: string;
}

/**
 * Profile-specific share metadata
 */
interface ProfileShareMetadata extends BaseShareMetadata {
  type: ShareEntityType.Profile;
  isOwnProfile?: boolean;
  connectionStatus?: string;
}

/**
 * Question-specific share metadata
 */
interface QuestionShareMetadata extends BaseShareMetadata {
  type: ShareEntityType.Question;
  questionTitle?: string;
  eventIds?: string[];
  isMeetverse?: boolean;
  visibility?: string;
  isAnonymous?: boolean;
}

/**
 * Event-specific share metadata
 */
interface EventShareMetadata extends BaseShareMetadata {
  type: ShareEntityType.Event;
  eventName?: string;
}

/**
 * Union type for all share metadata
 */
export type ShareMetadata = ProfileShareMetadata | QuestionShareMetadata | EventShareMetadata;

// =============================================================================
// URL UTILITIES
// =============================================================================

import { shortenUrl, type ShortenMetadata } from '@lib/urlShortener';

/**
 * Options for generating share URLs with viral loop tracking
 *
 * ADDING NEW TRACKING PARAMS:
 * New tracking parameters should be added here and "baked in" to the destination URL
 * at short URL creation time via `getShareUrl()`. Do NOT add them to the URL shortener's
 * passthrough allowlist - that exists only for backwards compatibility with legacy URLs.
 *
 * To add a new param (e.g., campaign_id):
 * 1. Add the field to this interface
 * 2. Update `getShareUrl()` to append it to the URL
 * 3. Update `buildShareUrlOptions()` to pass it through
 * 4. Update `parseAttributionFromUrl()` in session.ts to capture it
 * 5. Update `AnalyticsBootstrapper.tsx` to include it in events
 *
 * The param will be baked into each short URL's destination - no shortener changes needed.
 */
export interface ShareUrlOptions {
  /** User ID of the person sharing (for viral loop attribution) */
  sharerId?: string;
  /** Event ID being shared (for event viral loop tracking) */
  eventId?: string;
  /** Question ID being shared (for question viral loop tracking) */
  questionId?: string;
  /** Profile ID being shared (for profile viral loop tracking) */
  profileId?: string;
  /** Social platform for platform-specific source attribution */
  platform?: SocialPlatform;
  /** Sharer's referral code; defaults to the signed-in user's via the referralCode provider */
  referralCode?: string | null;
}

/**
 * Get the source attribution string for a social platform
 * Used for A/B testing share performance across different channels
 *
 * @param platform - The social platform being shared to
 * @returns Source string (e.g., "app_share_whatsapp", "app_share_linkedin")
 */
export const getSourceForPlatform = (platform?: SocialPlatform): string => {
  if (!platform) return 'app_share';

  const platformSources: Record<SocialPlatform, string> = {
    [SocialPlatform.WhatsApp]: 'app_share_whatsapp',
    [SocialPlatform.Telegram]: 'app_share_telegram',
    [SocialPlatform.LinkedIn]: 'app_share_linkedin',
    [SocialPlatform.Facebook]: 'app_share_facebook',
    [SocialPlatform.Twitter]: 'app_share_twitter',
    [SocialPlatform.Email]: 'app_share_email',
    [SocialPlatform.Copy]: 'app_share_copy',
    [SocialPlatform.Native]: 'app_share_native',
  };

  return platformSources[platform];
};

/**
 * Get shareable URL with source parameter and optional viral loop tracking
 *
 * @param path - The path to share (e.g., from getProfilePath, getQuestionPath, getEventPath)
 * @param options - Optional parameters for viral loop tracking
 * @returns Full URL with ?source=app_share (or platform-specific) and optional ref/ref_user/ref_event/ref_question/ref_profile parameters
 */
export const getShareUrl = (path: string, options?: ShareUrlOptions): string => {
  const baseUrl = `${getWebOrigin()}${path}`;
  const url = new URL(baseUrl);
  url.searchParams.set('source', getSourceForPlatform(options?.platform));

  // Add viral loop tracking parameters
  // Referral attribution: a non-empty explicit override wins, else the signed-in
  // user's code from the referralCode provider; omitted when neither is known.
  // Trim the override before `||` so a blank OR whitespace-only override is
  // treated as "no override" and falls back to the provider - neither an explicit
  // '' nor '   ' may silently suppress the signed-in user's ref= (the "always
  // append ref" chokepoint), and a whitespace-only value must never be emitted as
  // ref=%20%20%20 and corrupt attribution. Mirrors setCurrentReferralCode's own
  // trim-to-absent normalization.
  const referralCode = options?.referralCode?.trim() || getCurrentReferralCode();
  if (referralCode) {
    url.searchParams.set('ref', referralCode);
  }
  if (options?.sharerId) {
    url.searchParams.set('ref_user', options.sharerId);
  }
  if (options?.eventId) {
    url.searchParams.set('ref_event', options.eventId);
  }
  if (options?.questionId) {
    url.searchParams.set('ref_question', options.questionId);
  }
  if (options?.profileId) {
    url.searchParams.set('ref_profile', options.profileId);
  }

  return url.toString();
};

/**
 * Get a shortened shareable URL (async version)
 *
 * Uses the Meetball URL shortener service (go.meetball.fun) to create short URLs.
 * Falls back to full URL if shortening fails.
 *
 * @param path - The path to share
 * @param options - Optional parameters for viral loop tracking
 * @returns Promise resolving to shortened URL (e.g., https://go.meetball.fun/xK9mP2Qr)
 */
export const getShortShareUrl = async (
  path: string,
  options?: ShareUrlOptions,
): Promise<string> => {
  // First build the full URL with tracking params
  const fullUrl = getShareUrl(path, options);

  // Determine entity type for metadata
  let entityType: ShortenMetadata['entityType'];
  let entityId: string | undefined;

  if (options?.eventId) {
    entityType = 'event';
    entityId = options.eventId;
  } else if (options?.questionId) {
    entityType = 'question';
    entityId = options.questionId;
  } else if (options?.profileId) {
    entityType = 'profile';
    entityId = options.profileId;
  }

  // Call shortener with metadata for analytics
  return shortenUrl(fullUrl, {
    entityType,
    entityId,
    sharerId: options?.sharerId,
  });
};

// =============================================================================
// CLIPBOARD & NATIVE SHARE
// =============================================================================

/**
 * Copy URL to clipboard with toast notification
 *
 * @param url - The URL to copy
 * @param entityType - The type of entity being shared
 */
export const copyLinkToClipboard = async (
  url: string,
  entityType: ShareEntityType,
): Promise<void> => {
  const entityLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  try {
    await copyToClipboard(url);
    toast({
      title: 'Link copied!',
      description: `${entityLabel} link has been copied to your clipboard.`,
    });
  } catch (error) {
    logger.error('Failed to copy to clipboard:', error);
    toast({
      title: 'Copy failed',
      description: 'Unable to copy link. Please try again or copy the URL manually.',
      variant: 'error',
    });
  }
};

/**
 * Copy URL to clipboard using ClipboardItem API with async content resolution.
 *
 * This preserves the user gesture for Safari by calling navigator.clipboard.write()
 * synchronously, while the actual content (from URL shortening) resolves asynchronously.
 *
 * @param urlPromise - Promise that resolves to the URL to copy
 * @param entityType - The type of entity being shared
 * @param fallbackUrl - Synchronous fallback URL if ClipboardItem is not supported
 */
export const copyLinkToClipboardAsync = async (
  urlPromise: Promise<string>,
  entityType: ShareEntityType,
  fallbackUrl: string,
): Promise<void> => {
  const entityLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  try {
    if (isNativePlatform()) {
      // Native: no user-gesture timing concerns, just await and copy
      const url = await urlPromise;
      await copyToClipboard(url);
    } else if (typeof ClipboardItem !== 'undefined') {
      // Web: preserve user gesture timing with ClipboardItem async blob
      const item = new ClipboardItem({
        'text/plain': urlPromise.then(url => new Blob([url], { type: 'text/plain' })),
      });
      await navigator.clipboard.write([item]);
    } else {
      // Fallback for older browsers - wait for URL then use writeText
      const url = await urlPromise;
      await copyToClipboard(url);
    }

    toast({
      title: 'Link copied!',
      description: `${entityLabel} link has been copied to your clipboard.`,
    });
  } catch (error) {
    // If async clipboard fails, try with fallback URL synchronously
    logger.warn('Async clipboard failed, trying fallback:', error);
    try {
      await copyToClipboard(fallbackUrl);
      toast({
        title: 'Link copied!',
        description: `${entityLabel} link has been copied to your clipboard.`,
      });
    } catch (fallbackError) {
      logger.error('Failed to copy to clipboard:', fallbackError);
      toast({
        title: 'Copy failed',
        description: 'Unable to copy link. Please try again or copy the URL manually.',
        variant: 'error',
      });
    }
  }
};

/**
 * Share content using native Web Share API or clipboard fallback.
 *
 * @param text - The text to share
 * @param url - The URL to share
 * @param entityType - The type of entity being shared
 * @returns Promise resolving to `true` when the content was actually shared or
 *   copied to the clipboard, and `false` when the user CANCELLED the share sheet
 *   (native "Share canceled"/dismiss, or web `AbortError`). Callers that gate a
 *   side effect on a real share (e.g. the post-help invite cap) MUST branch on
 *   this - a cancel must never be treated as a completed share. Copying the
 *   invite link via the clipboard fallback counts as `true` (an intentional
 *   share). Callers that ignore the return value keep compiling (void widens to
 *   boolean).
 */
export const shareContent = async (
  text: string,
  url: string,
  entityType: ShareEntityType,
): Promise<boolean> => {
  // Use Capacitor Share plugin on native for reliable sharing
  if (isNativePlatform()) {
    const { Share } = await import('@capacitor/share');
    try {
      await Share.share({ text, url, dialogTitle: 'Share' });
      return true;
    } catch (error) {
      // User dismissed the share sheet - nothing was shared.
      if (/cancel|dismiss/i.test((error as Error).message)) {
        return false;
      }
      throw error;
    }
  }

  // Web: use navigator.share with clipboard fallback
  // IMPORTANT: call navigator.share() directly — extracting it into a variable
  // loses the `this` binding and throws "Illegal invocation" in Safari.
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ text, url });
      return true;
    } catch (error) {
      if ((error as Error).name === 'AbortError') return false;
      logger.warn('Web Share API failed, falling back to clipboard:', error);
    }
  }
  // Clipboard fallback: copying the link is an intentional share -> true.
  await copyLinkToClipboard(url, entityType);
  return true;
};

// =============================================================================
// SOCIAL PLATFORM URL BUILDERS
// =============================================================================

/**
 * Get WhatsApp share URL
 */
export const getWhatsAppShareUrl = (message: string): string => {
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
};

/**
 * Get Telegram share URL
 */
export const getTelegramShareUrl = (text: string, url: string): string => {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
};

/**
 * Get LinkedIn share URL
 */
export const getLinkedInShareUrl = (url: string): string => {
  return `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}`;
};

/**
 * Get Facebook share URL
 */
export const getFacebookShareUrl = (url: string): string => {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
};

/**
 * Get X (Twitter) share URL
 * Note: The text already contains the URL from the message template
 */
export const getTwitterShareUrl = (text: string): string => {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
};

/**
 * Get Email share URL
 */
export const getEmailShareUrl = (subject: string, body: string): string => {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

// =============================================================================
// NATIVE URL SCHEME HELPERS
// =============================================================================

/**
 * Returns a bare URL scheme (e.g., "whatsapp://") used to detect if a native app
 * is installed via AppLauncher.canOpenUrl().
 *
 * The five social media platforms (WhatsApp, Telegram, Twitter, Facebook, LinkedIn)
 * return a detection scheme; other SocialPlatform values (Email, Copy, Native) return null.
 * For WhatsApp, Telegram, and Twitter, getNativeSchemeUrl() also provides a deep link URL
 * for direct opening. For Facebook and LinkedIn, getNativeSchemeUrl() returns null -- these
 * platforms are opened via Universal Links (HTTPS URL through AppLauncher.openUrl()).
 */
const getAppDetectionScheme = (platform: SocialPlatform): string | null => {
  switch (platform) {
    case SocialPlatform.WhatsApp:
      return 'whatsapp://';
    case SocialPlatform.Telegram:
      return 'tg://';
    case SocialPlatform.Facebook:
      return 'fb://';
    case SocialPlatform.LinkedIn:
      return 'linkedin://';
    case SocialPlatform.Twitter:
      return 'twitter://';
    default:
      return null;
  }
};

/** Minimal share payload for direct platform sharing (e.g., referral codes) when a full ShareContext is unavailable. */
interface NativeSharePayload {
  text: string;
  url?: string;
}

/** Discriminates ShareContext (has entityType/path) from NativeSharePayload. */
const isShareContext = (value?: ShareContext | NativeSharePayload): value is ShareContext => {
  return Boolean(value && 'entityType' in value && 'path' in value);
};

/**
 * Returns a full native deep link URL for sharing to a platform's app
 * (e.g., "whatsapp://send?text=..."), or null if the platform has no custom
 * deep link (Facebook, LinkedIn -- these use Universal Links via HTTPS URLs instead).
 */
const getNativeSchemeUrl = (
  platform: SocialPlatform,
  fallbackUrl: string,
  shareData?: ShareContext | NativeSharePayload,
): string | null => {
  const sharedUrl = shareData?.url ?? fallbackUrl;

  switch (platform) {
    case SocialPlatform.WhatsApp: {
      const message = isShareContext(shareData)
        ? getShareMessage({ ...shareData, url: sharedUrl }, TextPlatform.WhatsApp)
        : (shareData?.text ?? fallbackUrl);
      return `whatsapp://send?text=${encodeURIComponent(message)}`;
    }
    case SocialPlatform.Telegram: {
      const message = isShareContext(shareData)
        ? getShareMessage({ ...shareData, url: sharedUrl }, TextPlatform.Telegram)
        : (shareData?.text ?? '');
      return `tg://msg_url?url=${encodeURIComponent(sharedUrl)}&text=${encodeURIComponent(message)}`;
    }
    case SocialPlatform.Twitter: {
      const message = isShareContext(shareData)
        ? getShareMessage({ ...shareData, url: sharedUrl }, TextPlatform.Twitter)
        : (shareData?.text ?? fallbackUrl);
      return `twitter://post?message=${encodeURIComponent(message)}`;
    }
    default:
      return null;
  }
};

/**
 * Opens a URL externally, using the best method for the current platform.
 *
 * On native (mutually exclusive paths, with shared fallback):
 *   1. Native app scheme (WhatsApp, Telegram, Twitter) -- opens directly via custom URL scheme
 *   2. Universal Links (Facebook, LinkedIn) -- detect app via scheme, open HTTPS URL via AppLauncher
 *      (AppLauncher.openUrl triggers Universal Links; Browser.open does not)
 *   3. In-app browser fallback -- if Path 1 or 2 fails or is not applicable
 *
 * On web:
 *   Opens in a new tab via window.open() (unchanged behavior)
 */
export const openExternalUrl = async (
  httpsUrl: string,
  platform?: SocialPlatform,
  shareData?: ShareContext | NativeSharePayload,
): Promise<void> => {
  if (isNativePlatform()) {
    try {
      if (platform) {
        const { AppLauncher } = await import('@capacitor/app-launcher');

        // Path 1: Try native app scheme (WhatsApp, Telegram, Twitter)
        // Uses the bare detection scheme for canOpenUrl() probing, then opens the
        // full native deep link URL. Both getAppDetectionScheme and getNativeSchemeUrl
        // must return non-null for this path to execute.
        const nativeUrl = getNativeSchemeUrl(platform, httpsUrl, shareData);
        const detectionScheme = getAppDetectionScheme(platform);

        if (nativeUrl && detectionScheme) {
          try {
            const { value: canOpen } = await AppLauncher.canOpenUrl({ url: detectionScheme });
            if (canOpen) {
              await AppLauncher.openUrl({ url: nativeUrl });
              return;
            }
          } catch (error) {
            logger.warn('AppLauncher native scheme failed, falling through to browser:', error);
          }
        }

        // Path 2: Universal Links for platforms with a detection scheme but no native
        // deep link URL (Facebook, LinkedIn). Detect the app via bare scheme, then open
        // the HTTPS URL through AppLauncher.openUrl() to trigger Universal Links.
        // (Browser.open does not trigger Universal Links.)
        if (detectionScheme && !nativeUrl) {
          try {
            const { value: appInstalled } = await AppLauncher.canOpenUrl({
              url: detectionScheme,
            });
            if (appInstalled) {
              await AppLauncher.openUrl({ url: httpsUrl });
              return;
            }
          } catch (error) {
            logger.warn(
              'AppLauncher app detection failed, falling through to in-app browser:',
              error,
            );
          }
        }
      }

      // Path 3: Fallback -- in-app browser (SFSafariViewController on iOS, Chrome Custom Tabs on Android)
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: httpsUrl });
      return;
    } catch (error) {
      logger.error('Native sharing failed entirely, unable to open URL:', error);
      throw error;
    }
  }
  // Web: open in new tab.
  // NOTE: with `noopener` set, window.open() returns null on SUCCESS as well as when the
  // popup is blocked (see the comment near the share-window path below) — so the return
  // value cannot distinguish the two, and a blocked-popup check here would false-positive on
  // every successful open. Web popup-blocking is therefore an accepted silent no-op (the user
  // can retry); we keep `noopener,noreferrer` for opener isolation rather than trade it away
  // for unreliable detection.
  window.open(httpsUrl, '_blank', 'noopener,noreferrer');
};

/**
 * Runs an open from a click handler without waiting on it. The open helper logs its own failure
 * and re-throws; this drops the re-throw, so a failed native open never becomes an unhandled
 * promise rejection. Nothing is awaited before the open starts, so on web it stays inside the tap,
 * which browsers require for a new tab.
 */
const openInBackground = (open: (url: string) => Promise<void>, url: string): void => {
  void open(url).catch(() => {
    // Logged by the open helper; nothing the caller can do about it.
  });
};

/** `openExternalUrl` for a click handler: fire and forget, failures contained (see above). */
export const openExternalUrlInBackground = (url: string): void => {
  openInBackground(openExternalUrl, url);
};

/**
 * Opens a URL in the system browser: on native, Safari on iOS or the default browser on Android,
 * where the member is already signed in to the site (`openExternalUrl` ends at the in-app browser,
 * and on iOS that sheet keeps its own cookies); on web, a new tab exactly as `openExternalUrl`
 * opens it. The URL is passed unchanged, since its query string can carry a partner code.
 *
 * Native uses AppLauncher.openUrl, falling back to the in-app browser when it throws or reports it
 * could not open. An https URL needs no Info.plist entry: LSApplicationQueriesSchemes governs
 * canOpenUrl on custom schemes only. A universal link may open its own app instead of Safari,
 * which is still outside the webview.
 */
export const openInSystemBrowser = async (url: string): Promise<void> => {
  // Web: delegated with nothing awaited first, so the tab still opens inside the tap.
  if (!isNativePlatform()) return openExternalUrl(url);
  try {
    const { AppLauncher } = await import('@capacitor/app-launcher');
    const { completed } = await AppLauncher.openUrl({ url });
    if (completed) return;
    logger.warn('AppLauncher could not open the URL; opening it in the in-app browser');
  } catch (error) {
    logger.warn('AppLauncher failed; opening the URL in the in-app browser:', error);
  }
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } catch (error) {
    logger.error('Unable to open the URL in any browser:', error);
    throw error;
  }
};

/** `openInSystemBrowser` for a click handler: fire and forget, failures contained (see above). */
export const openInSystemBrowserInBackground = (url: string): void => {
  openInBackground(openInSystemBrowser, url);
};

// =============================================================================
// SHARE EXECUTOR - The main function to share to a platform
// =============================================================================

/**
 * Build share URL options from context for a specific platform
 */
const buildShareUrlOptions = (context: ShareContext, platform: SocialPlatform): ShareUrlOptions => {
  const base: ShareUrlOptions = {
    sharerId: context.sharerId,
    platform,
  };

  if (context.entityType === ShareEntityType.Event) {
    return { ...base, eventId: context.eventId };
  } else if (context.entityType === ShareEntityType.Question) {
    return { ...base, questionId: context.questionId };
  } else if (context.entityType === ShareEntityType.Profile) {
    return { ...base, profileId: context.profileId };
  } else if (context.entityType === ShareEntityType.App) {
    // App invite - context.path already carries /invite/{code} (+ ?u=slug)
    // via buildInvitePath; ref= is appended globally by getShareUrl.
    return base;
  } else {
    // Community — no specific ref param yet, just base tracking
    return base;
  }
};

/**
 * Get the share URL for a platform given context and short URL
 */
const getShareUrlForPlatform = (
  platform: SocialPlatform,
  url: string,
  context: ShareContext,
): string | null => {
  const contextWithUrl = { ...context, url };

  switch (platform) {
    case SocialPlatform.WhatsApp: {
      const message = getShareMessage(contextWithUrl, TextPlatform.WhatsApp);
      return getWhatsAppShareUrl(message);
    }
    case SocialPlatform.Telegram: {
      const message = getShareMessage(contextWithUrl, TextPlatform.Telegram);
      return getTelegramShareUrl(message, url);
    }
    case SocialPlatform.LinkedIn:
      return getLinkedInShareUrl(url);
    case SocialPlatform.Facebook:
      return getFacebookShareUrl(url);
    case SocialPlatform.Twitter: {
      const message = getShareMessage(contextWithUrl, TextPlatform.Twitter);
      return getTwitterShareUrl(message);
    }
    default:
      return null;
  }
};

/**
 * Share to a specific social platform using context
 *
 * Generates a platform-specific short URL on-demand with the correct source parameter
 * baked into the destination URL (not as query params on the short URL).
 *
 * For platforms that open new windows (WhatsApp, Telegram, etc.), we open the window
 * synchronously to preserve user gesture timing (required by Safari's popup blocker),
 * then navigate to the final URL once shortening completes.
 *
 * When prefetchedUrl is provided (from usePrefetchShareUrls hook), the async fetch
 * is skipped entirely - opening the share URL directly. This eliminates Safari's
 * popup blocker issues by keeping the entire operation synchronous.
 *
 * @param platform - The social platform to share to
 * @param context - The share context with all necessary information
 * @param prefetchedUrl - Optional pre-fetched short URL (skips async fetch if provided)
 * @returns Promise resolving to `true` when the share completed and `false` only
 *   when the user CANCELLED it. Today only the Native path (Web Share / Capacitor
 *   Share) surfaces a real cancel via `shareContent`; every other path (Copy,
 *   Email, and the social-app/window opens) has no cancel signal, so it reports
 *   `true` - opening the target or copying the link is treated as completed.
 *   Callers use this to decide whether a completion-gated side effect (e.g. the
 *   post-help invite cap) should record a share.
 */
export const shareToSocialPlatform = async (
  platform: SocialPlatform,
  context: ShareContext,
  prefetchedUrl?: string,
): Promise<boolean> => {
  const { entityType, path } = context;
  const options = buildShareUrlOptions(context, platform);

  // For Copy action, use async clipboard API that preserves user gesture timing
  if (platform === SocialPlatform.Copy) {
    if (prefetchedUrl) {
      // Pre-fetched URL available - use it directly
      logger.debug('[Share] Copy INSTANT - using prefetched URL', {
        platform,
        prefetchedUrl,
        method: 'direct-copy',
      });
      await copyLinkToClipboard(prefetchedUrl, entityType);
    } else {
      // Fall back to async clipboard with promise
      logger.warn('[Share] Copy FALLBACK - fetching URL async', {
        platform,
        method: 'async-clipboard',
      });
      const urlPromise = getShortShareUrl(path, options);
      const fallbackUrl = getShareUrl(path, options);
      await copyLinkToClipboardAsync(urlPromise, entityType, fallbackUrl);
    }
    // Copying the link is an intentional share.
    return true;
  }

  // For Email, we use location.href which doesn't have popup blocker issues
  if (platform === SocialPlatform.Email) {
    const url = prefetchedUrl || (await getShortShareUrl(path, options));
    const contextWithUrl = { ...context, url };
    const { subject, body } = getEmailTemplates(contextWithUrl);
    window.location.href = getEmailShareUrl(subject, body);
    // mailto navigation has no cancel signal - treat as completed.
    return true;
  }

  // For Native share, use the Web Share API
  if (platform === SocialPlatform.Native) {
    if (prefetchedUrl) {
      logger.debug('[Share] Native INSTANT - using prefetched URL', {
        platform,
        prefetchedUrl,
        method: 'direct-native',
      });
    } else {
      logger.warn('[Share] Native FALLBACK - fetching URL async', {
        platform,
        method: 'async-native',
      });
    }
    const url = prefetchedUrl || (await getShortShareUrl(path, options));
    const contextWithUrl = { ...context, url };
    const message = getShareMessage(contextWithUrl, TextPlatform.Native);
    // The only path that can report a real cancel - propagate it to the caller.
    return shareContent(message, url, entityType);
  }

  // For social platforms that open windows (WhatsApp, Telegram, LinkedIn, Facebook, Twitter):
  // When prefetchedUrl is available, we can open directly without the about:blank trick.
  // This is faster and avoids any potential popup blocker issues.
  if (prefetchedUrl) {
    logger.debug('[Share] INSTANT - using prefetched URL (no about:blank)', {
      platform,
      prefetchedUrl,
      method: 'direct-open',
    });
    const shareUrl = getShareUrlForPlatform(platform, prefetchedUrl, context);
    if (shareUrl) {
      await openExternalUrl(shareUrl, platform, { ...context, url: prefetchedUrl });
    } else {
      logger.error('Unknown platform for share:', platform);
    }
    // Opening the platform app/window is the completion (no cancel signal).
    return true;
  }

  // Fallback: No pre-fetched URL available
  if (isNativePlatform()) {
    // Native: no popup blocker concern, just await the URL and open
    const url = await getShortShareUrl(path, options);
    const shareUrl = getShareUrlForPlatform(platform, url, context);
    if (shareUrl) {
      await openExternalUrl(shareUrl, platform, { ...context, url });
    }
    return true;
  }

  // Web: Safari's popup blocker requires window.open() to be called synchronously within user gesture.
  // We open about:blank immediately (preserves gesture), then navigate after URL shortening.
  // Note: We can't use noopener here because we need to control the window's location.
  logger.warn('[Share] FALLBACK - using about:blank trick (prefetch not ready)', {
    platform,
    method: 'about-blank-trick',
  });

  // Open blank window synchronously to preserve user gesture
  const newWindow = window.open('about:blank', '_blank');

  if (!newWindow) {
    // Popup was blocked - fall back to opening with long URL directly
    logger.warn('Popup blocked, falling back to direct open with long URL');
    const fallbackUrl = getShareUrl(path, options);
    const fallbackShareUrl = getShareUrlForPlatform(platform, fallbackUrl, context);
    if (fallbackShareUrl) {
      window.open(fallbackShareUrl, '_blank', 'noopener,noreferrer');
    }
    // Web open (or accepted popup-block no-op) - preserve prior completed behavior.
    return true;
  }

  // Get fallback URL synchronously (long URL) in case shortening fails
  const fallbackUrl = getShareUrl(path, options);
  const fallbackShareUrl = getShareUrlForPlatform(platform, fallbackUrl, context);

  if (!fallbackShareUrl) {
    logger.error('Unknown platform for share:', platform);
    newWindow.close();
    return true;
  }

  try {
    // Try to get shortened URL
    const shortUrl = await getShortShareUrl(path, options);
    const shortShareUrl = getShareUrlForPlatform(platform, shortUrl, context);
    // Navigate to the share URL (prefer short, fall back to long)
    newWindow.location.href = shortShareUrl || fallbackShareUrl;
  } catch (error) {
    // Shortening failed, use fallback URL
    logger.warn('URL shortening failed, using fallback URL:', error);
    newWindow.location.href = fallbackShareUrl;
  }

  // Web window navigated to the share URL - completed (no cancel signal).
  return true;
};
